import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import api from "utils/api";
import StudyHubTableData from "Views/UsefulCorners/StudyHub/data/studyHubTableData";

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "NOTES", label: "Notes" },
  { value: "EXAM_PAPER", label: "Exam paper" },
  { value: "QUIZ", label: "Quiz" },
  { value: "SYLLABUS", label: "Syllabus" },
  { value: "TIMETABLE", label: "Timetable" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All status" },
  { value: "LIVE", label: "Live" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "DRAFT", label: "Draft" },
  { value: "HIDDEN", label: "Hidden" },
];

function StudyHub() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get("/study-materials");
      setItems(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load study material");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filtered = items.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesQuery =
      !query ||
      item.title?.toLowerCase().includes(query) ||
      item.chapter?.toLowerCase().includes(query) ||
      item.classId?.name?.toLowerCase().includes(query) ||
      item.subjectId?.name?.toLowerCase().includes(query);
    const matchesType = !typeFilter || item.type === typeFilter;
    const matchesStatus = !statusFilter || item.visibility === statusFilter;
    return matchesQuery && matchesType && matchesStatus;
  });

  const handleView = async (item) => {
    const file = item.files?.[0];
    if (!file) {
      toast.error("No file uploaded");
      return;
    }
    try {
      const response = await api.get(`/study-materials/${item._id}/file/${file.id}`);
      if (response.data?.data?.url) window.open(response.data.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not open the file");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/study-materials/${selectedId}`);
      toast.success("Study material deleted");
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete");
    } finally {
      setDeleteOpen(false);
      setSelectedId(null);
    }
  };

  const handleStatusConfirm = async () => {
    if (!statusTarget) return;
    const next = statusTarget.visibility === "LIVE" ? "HIDDEN" : "PUBLISHED";
    try {
      const response = await api.patch(`/study-materials/${statusTarget._id}/status`, { status: next });
      toast.success(response.data.message || "Updated");
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setStatusTarget(null);
    }
  };

  const { columns, rows } = StudyHubTableData(
    filtered,
    handleView,
    (item) => setStatusTarget(item),
    (id) => {
      setSelectedId(id);
      setDeleteOpen(true);
    },
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={2}>
            <ArgonBox display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
              <ArgonBox>
                <ArgonTypography variant="h5" fontWeight="medium">
                  Study Hub {!loading && `(${filtered.length})`}
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text" fontWeight="regular">
                  Notes, quizzes, exam papers and timetables that parents see in the app
                </ArgonTypography>
              </ArgonBox>
              <ArgonButton size="small" component={Link} to="/study-hub/add" variant="contained" color="info">
                Add material
              </ArgonButton>
            </ArgonBox>
            <ArgonBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <ArgonInput
                placeholder="Search title, class or subject"
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <Dropdown
                options={TYPE_OPTIONS}
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                width={160}
              />
              <Dropdown
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                width={160}
              />
            </ArgonBox>
          </ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress color="info" />
            </ArgonBox>
          ) : filtered.length === 0 ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="body2" color="text">
                {searchQuery || typeFilter || statusFilter ? "No study material matches this filter" : "No study material yet"}
              </ArgonTypography>
            </ArgonBox>
          ) : (
            <Table columns={columns} rows={rows} />
          )}
        </Card>
      </ArgonBox>
      <Footer />
      <DeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete study material?"
        description="Parents will stop seeing this file."
        confirmText="Delete"
        cancelText="Cancel"
      />
      <DeleteDialog
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        onConfirm={handleStatusConfirm}
        title={statusTarget?.visibility === "LIVE" ? "Hide from parents?" : "Show to parents?"}
        description={
          statusTarget?.visibility === "LIVE"
            ? "This will disappear from the parent app until you publish it again."
            : "Matching parents will be able to open this, and they will get a notification if it is visible now."
        }
        confirmText={statusTarget?.visibility === "LIVE" ? "Hide" : "Publish"}
        cancelText="Cancel"
        confirmIcon={statusTarget?.visibility === "LIVE" ? <VisibilityOffIcon /> : <CheckCircleIcon />}
        confirmColor={statusTarget?.visibility === "LIVE" ? "warning" : "success"}
        warningText="You can change this again later."
      />
    </DashboardLayout>
  );
}

export default StudyHub;
